/*
 * Copyright (C) Motorola Solutions, INC.
 * All Rights Reserved.
 */

let logFileStream = null;

const LOG_RETENTION_FILE_COUNT = 32;
const LOG_RETENTION_INTERVAL = 6 * 60 * 60 * 1000;
const LOG_RETENTION_SIZE = 1024 * 1024;

const now = () => new Date().toISOString();

function getNewLogFileName() {
    const newDate = new Date();
    const suffix = newDate.toISOString().replace(/[:.]/g, '-');
    return 'vesta-nxt-client-logs' + "_" + suffix;
}

let logFileName = getNewLogFileName();

const rotateLogFile = async function () {
    if (logFileStream) {
        await logFileStream.close();
        logFileStream = null;
    }
    logFileName = getNewLogFileName();
};

globalThis.addEventListener('beforeunload', () => rotateLogFile());

const deleteOldLogFiles = async function () {
    const dir = await navigator.storage.getDirectory();
    const logFiles = [];
    for await (const [name, handle] of dir.entries()) {
        if (name.startsWith('vesta-nxt-client-logs')) {
            logFiles.push(name);
        }
    }
    if (logFiles.length > LOG_RETENTION_FILE_COUNT) {
        logFiles.sort((a, b) => b.localeCompare(a));
        const filesToDelete = logFiles.slice(LOG_RETENTION_FILE_COUNT);
        for (const fileName of filesToDelete) {
            await dir.removeEntry(fileName);
        }
    }
};

async function getCurrentLogFileSize() {
    const dir = await navigator.storage.getDirectory();
    try {
        const fileName = logFileName + ".crswap";
        const fileHandle = await dir.getFileHandle(fileName, { create: false });
        const file = await fileHandle.getFile();
        return file.size;
    } catch (e) {
        return 0;
    }
}

async function getLogFileStream() {
    if (logFileStream) return logFileStream;
    const fileName = logFileName;
    const dh = await navigator.storage.getDirectory();
    const fh = await dh.getFileHandle(fileName, { create: true });
    logFileStream = await fh.createWritable({ keepExistingData: true });
    const file = await fh.getFile();
    // createWritable({ keepExistingData: true }) preserves file content but starts the write cursor at position 0.
    // Without seeking to the end, reopening the stream (e.g. after flush) would overwrite existing logs.
    if (file.size > 0) {
        await logFileStream.seek(file.size);
    }
    return logFileStream;
}

const LOG_STREAMING_QUEUE_MAX_SIZE = 1000;
globalThis.__logStreamingQueue = globalThis.__logStreamingQueue || [];
globalThis.__logStreamingFilter = globalThis.__logStreamingFilter || (() => false);

function enqueueLogForStreaming(timestamp, logMessage, level) {
    if (!globalThis.__logStreamingFilter(level)) return;

    const queue = globalThis.__logStreamingQueue;
    if (queue.length >= LOG_STREAMING_QUEUE_MAX_SIZE) {
        queue.splice(0, queue.length - LOG_STREAMING_QUEUE_MAX_SIZE + 1);
    }
    queue.push(buildJsonLogPayload(timestamp, level, logMessage));
}

function buildJsonLogPayload(timestamp, level, message) {
    return JSON.stringify({
        '@timestamp': timestamp,
        'log.level': level === 'LOG' ? 'INFO' : level,
        msg: message,
        'service.name': 'calltaking-client'
    });
}

function interceptLog(logType) {
    const original = console[logType].bind(console);
    return function () {
        let logMessage = Array.from(arguments)
            .map((arg) => arg && arg.name && arg.message ? [arg.name, arg.message, arg.stack].flat().filter((val) => !!val).join(' ') : arg)
            .join(' ');
        const timestamp = now();
        const level = logType.toUpperCase();

        getLogFileStream().then((stream) => stream.write(`${timestamp} ${level} ${logMessage}\n`)).catch(() => {});
        enqueueLogForStreaming(timestamp, logMessage, level);
        original.apply(console, arguments);
    };
}

['log', 'error', 'warn', 'debug', 'info'].forEach((logType) => (console[logType] = interceptLog(logType)));

async function flushLogStream() {
    try {
        await writeQueue;
        if (logFileStream) {
            await logFileStream.close();
            logFileStream = null;
        }
    } catch (e) {
        // Best effort flush
    }
}

globalThis.addEventListener('beforeunload', () => {
    void flushLogStream();
});

// Flush logs when the page becomes hidden (e.g. tab switch, navigation to IDM for re-auth).
// This ensures buffered writes are committed to OPFS before the page context may be destroyed.
globalThis.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
        void flushLogStream();
    }
});

let nextLogRotationTimestamp = Date.now() + LOG_RETENTION_INTERVAL;
setInterval(async () => {
    const logFileSize = await getCurrentLogFileSize();
    if ((Date.now() - nextLogRotationTimestamp >= LOG_RETENTION_INTERVAL) || logFileSize > LOG_RETENTION_SIZE) {
        await rotateLogFile();
        await deleteOldLogFiles();
        nextLogRotationTimestamp = Date.now();
    }
}, 15000);

globalThis.rotateLogFile = rotateLogFile;
globalThis.flushLogStream = flushLogStream;
